import Grid from '@mui/material/Grid2';
import { type ReactNode } from 'react';

import { SelectField } from '@/components/forms/SelectField';
import { type VehicleSearchFormState } from '@/hooks/useVehicleSearchForm';

import { type FieldLayout, fieldSize, type VehicleSearchFieldName } from './searchFields';

type Props = {
  form: VehicleSearchFormState;
  fields: readonly VehicleSearchFieldName[];
  layout: FieldLayout;
};

export function VehicleSearchFields({ form, fields, layout }: Props) {
  const { control, options, make, clearModel } = form;

  const render: Record<VehicleSearchFieldName, () => ReactNode> = {
    make: () => (
      <SelectField
        control={control}
        name="make"
        label="Make"
        emptyLabel="Any brand"
        options={options.makes}
        onValueChange={clearModel}
      />
    ),
    model: () => (
      <SelectField
        control={control}
        name="model"
        label="Model"
        emptyLabel={make ? 'Any model' : 'Choose a make first'}
        options={options.models}
        disabled={!make || options.models.length === 0}
      />
    ),
    yearMin: () => (
      <SelectField
        control={control}
        name="yearMin"
        label="Year from"
        emptyLabel="Any"
        options={options.years}
      />
    ),
    yearMax: () => (
      <SelectField
        control={control}
        name="yearMax"
        label="Year to"
        emptyLabel="Any"
        options={options.years}
      />
    ),
    budgetMax: () => (
      <SelectField
        control={control}
        name="budgetMax"
        label="Budget"
        emptyLabel="Any budget"
        options={options.budgets}
        helperText="Compared with the car's USD price"
      />
    ),
    body: () => (
      <SelectField
        control={control}
        name="body"
        label="Body type"
        emptyLabel="Any body type"
        options={options.bodies}
      />
    ),
    fuel: () => (
      <SelectField
        control={control}
        name="fuel"
        label="Fuel"
        emptyLabel="Any fuel"
        options={options.fuels}
      />
    ),
    transmission: () => (
      <SelectField
        control={control}
        name="transmission"
        label="Transmission"
        emptyLabel="Any transmission"
        options={options.transmissions}
      />
    ),
    country: () => (
      <SelectField
        control={control}
        name="country"
        label="Country"
        emptyLabel="Any country"
        options={options.countries}
      />
    ),
  };

  return (
    <>
      {fields.map((name) => (
        <Grid key={name} size={fieldSize(name, layout)}>
          {render[name]()}
        </Grid>
      ))}
    </>
  );
}
